<template>
  <div class="m-page">
    <div class="m-toolbar">
      <div class="m-toolbar__row">
        <el-input
          v-model="keyword"
          clearable
          placeholder="派工单号、订单号、客户、仓库料号…"
          class="m-toolbar__search"
          @clear="reload"
          @keyup.enter="reload"
        >
          <template #append>
            <el-button @click="reload">搜</el-button>
          </template>
        </el-input>
        <el-button :loading="loading" class="m-toolbar__refresh" @click="load">刷新</el-button>
      </div>
      <el-select v-model="filterStatus" placeholder="状态" clearable style="width: 100%" @change="reload">
        <el-option label="草稿" value="draft" />
        <el-option label="已指派" value="assigned" />
        <el-option label="进行中" value="in_progress" />
        <el-option label="已完成" value="completed" />
        <el-option v-if="canAuditReview" label="待现场核销" value="pending_post_audit" />
        <el-option label="已取消" value="cancelled" />
      </el-select>
      <div class="m-switch-row">
        <el-switch
          v-model="onlyMine"
          active-text="仅看我的"
          inactive-text="查看全部"
          @change="reload"
        />
        <el-switch
          v-model="onlyIncomplete"
          active-text="仅看未完成"
          inactive-text="含已完成"
          @change="reload"
        />
      </div>
      <el-select v-model="listSort" class="m-sort-select" placeholder="排序" @change="onSortChange">
        <el-option label="要求完成 ↑（默认）" value="due_site" />
        <el-option label="要求完成 ↓" value="due_site_desc" />
        <el-option label="创建时间 ↓" value="created_desc" />
        <el-option label="派工单号 ↑" value="no_asc" />
        <el-option label="派工单号 ↓" value="no_desc" />
      </el-select>
      <p class="m-mine-hint">
        列表<strong>仅展示桌面端已审核通过</strong>的派工单；未审单不会下发到现场（避免提前安排分卷）。仓库建议「仅看我的」；管理员可切换查看全部。默认「仅看未完成」，可隐藏卡片上为<strong>已完成</strong>的单（仍保留待现场核销等需跟进的单）；关掉则含已完成。可先点<strong>所需材料</strong>再进跑码；需称重/量体积的用<strong>包装称重</strong>。</p>
    </div>

    <div v-loading="loading" class="m-cards">
      <div v-for="row in rows" :key="row.dispatch_order_id" class="m-card" :class="{ readonly: !canOperateDispatch }">
        <div class="m-card__main" @click="openDispatchDetail(row)">
          <div class="m-card__title">
            <span>{{ row.dispatch_order_no }}</span>
            <el-tag size="small" :type="cardStatusTag(row)">{{ cardStatusText(row) }}</el-tag>
          </div>
          <div v-if="dueLine(row).has" class="m-card__due" :class="dueLine(row).lineClass">
            <el-tag v-if="dueLine(row).label" size="small" :type="dueLine(row).tagType">{{ dueLine(row).label }}</el-tag>
            <span v-if="dueLine(row).sub" class="m-card__due-text">{{ dueLine(row).sub }}</span>
          </div>
          <div class="m-card__line muted">{{ mobileDispatchCardOneLine(row) }}</div>
          <div class="m-card__line">
            <span>指派 {{ row.assigned_user_name || '未指派' }}</span>
            <span>{{ row.item_count || 0 }} 条明细</span>
          </div>
          <div v-if="slittingProgressShow(row)" class="m-card__line m-card__slitting">
            <span class="muted">分卷进度</span>
            <span v-if="slittingProgressDone(row)" class="m-slitting-done">已分卷完成</span>
            <span v-else class="m-slitting-frac">{{ slittingProgressFraction(row) }}</span>
          </div>
          <div class="m-card__line muted">
            <el-tag size="small" :type="auditTag(row.audit_status)">{{ auditText(row.audit_status) }}</el-tag>
            <span v-if="!canOperateDispatch" class="m-readonly-text">只读</span>
          </div>
        </div>
        <div class="m-card__foot" @click.stop>
          <el-button
            type="primary"
            plain
            size="small"
            :loading="materialsOpeningId === row.dispatch_order_id"
            @click="openMaterials(row)"
          >
            所需材料
          </el-button>
          <el-button
            v-if="row.packaging_require_any"
            :type="packagingMeasuresIncomplete(row) ? 'warning' : 'default'"
            plain
            size="small"
            :class="{ 'm-pack-btn--done': !packagingMeasuresIncomplete(row) }"
            @click="openDispatchPackaging(row)"
          >
            包装称重
          </el-button>
        </div>
      </div>

      <el-empty v-if="!loading && rows.length === 0" description="暂无派工单" />

      <div v-if="pagination.pages > 1" class="m-pager">
        <el-button :disabled="pagination.page <= 1" @click="pagePrev">上一页</el-button>
        <span class="m-pager__info">{{ pagination.page }} / {{ pagination.pages }}</span>
        <el-button :disabled="pagination.page >= pagination.pages" @click="pageNext">下一页</el-button>
      </div>
    </div>

    <el-dialog
      v-model="materialsVisible"
      :title="`所需材料 — ${materialsTitle}`"
      width="96%"
      align-center
      append-to-body
      :lock-scroll="false"
      :close-on-click-modal="false"
      class="m-mat-dialog"
      @closed="onMaterialsClosed"
    >
      <div class="m-mat-scroll">
        <p v-if="materialsDispatchNotes" class="m-dispatch-notes m-dispatch-notes--secondary">
          整单备注（派工单头，非用料）：{{ materialsDispatchNotes }}
        </p>
        <p class="m-mat-hint muted sm">各料号下的「用料备注」来自派工明细，与订单正文备注不同。</p>
        <p v-if="materialsRows.length === 0" class="muted">暂无明细或无权查看。</p>
        <div v-for="r in materialsRows" :key="`${r.key}-${r.rowNo}`" class="m-mat-row">
          <div class="m-mat-row__head">
            <span class="m-mat-idx">第 {{ r.rowNo }} 种</span>
            <span class="m-mat-req">需求 {{ r.required }}</span>
          </div>
          <div class="m-mat-line"><span class="lbl">简述</span>{{ r.brief }}</div>
          <div class="m-mat-line"><span class="lbl">仓库料号</span><strong>{{ r.whCode }}</strong></div>
          <div class="m-mat-line"><span class="lbl">颜色</span>{{ r.colorLine }}</div>
          <div class="m-mat-line"><span class="lbl">库位</span></div>
          <div v-if="r.stockLines.length" class="m-mat-locs">
            <div v-for="(ln, i) in r.stockLines" :key="i" class="m-mat-loc">{{ ln }}</div>
          </div>
          <p v-else class="muted sm">暂无库位库存（请确认配料已选批次/料号）</p>
          <template v-if="r.remarkLines.length">
            <div class="m-mat-line m-mat-line--mt"><span class="lbl">用料备注</span></div>
            <div v-for="(rm, j) in r.remarkLines" :key="j" class="m-mat-remark">{{ rm }}</div>
          </template>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onActivated, computed, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { getDispatchOrders, getDispatchOrder } from '@/api/dispatchOrders'
import { useUserStore } from '@/store/user'
import { hasPermission } from '@/utils/permission'
import { classifyDispatchDue } from '@/utils/dispatchDueUrgency'

import { useMobileWorkbench } from '@/composables/useMobileWorkbench'

defineOptions({ name: 'MobileDispatchList' })

const { mPath } = useMobileWorkbench()

const DISPATCH_LIST_SORT_KEY = 'erp_m_dispatch_list_sort_v1'

const loading = ref(false)
const router = useRouter()
const userStore = useUserStore()
const keyword = ref('')
const filterStatus = ref('')
const onlyMine = ref(true)
/** 与卡片「已完成」一致：隐藏流程已结且本分卷已齐、且非待现场核销的单；默认仅看未完成 */
const onlyIncomplete = ref(true)
const rows = ref([])
const canOperateDispatch = computed(() => hasPermission(userStore.user, 'orders.dispatch.edit'))
const canAuditReview = computed(() => hasPermission(userStore.user, 'orders.dispatch.review'))
/** 与移动端派工路由 permissionAny 一致：有审核权可无编辑权进入详情核销 */
const canOpenDispatchDetail = computed(
  () =>
    hasPermission(userStore.user, 'orders.dispatch.view') ||
    hasPermission(userStore.user, 'orders.dispatch.edit') ||
    hasPermission(userStore.user, 'orders.dispatch.review') ||
    hasPermission(userStore.user, 'operations.execute')
)
const pagination = reactive({
  page: 1,
  per_page: 20,
  total: 0,
  pages: 1
})

const materialsVisible = ref(false)
const materialsTitle = ref('')
const materialsRows = ref([])
const materialsDispatchNotes = ref('')
/** 所需材料按钮 loading（避免弹窗内 v-loading 在部分 WebKit 上引发白屏） */
const materialsOpeningId = ref(null)
const listSort = ref('due_site')

function initListSortFromStorage() {
  try {
    const s = localStorage.getItem(DISPATCH_LIST_SORT_KEY)
    if (['due_site', 'due_site_desc', 'created_desc', 'no_asc', 'no_desc'].includes(s)) {
      listSort.value = s
    }
  } catch {
    /* ignore */
  }
}

function onSortChange() {
  try {
    localStorage.setItem(DISPATCH_LIST_SORT_KEY, listSort.value)
  } catch {
    /* ignore */
  }
  reload()
}

function dueLine(row) {
  const u = classifyDispatchDue(row)
  if (u.kind === 'none') {
    return { has: false, label: '', tagType: 'info', sub: '', lineClass: '' }
  }
  return {
    has: true,
    label: u.label,
    tagType: u.tagType,
    sub: u.dateLine || '',
    lineClass: u.lineClass
  }
}

function statusText(s) {
  const map = {
    draft: '草稿',
    assigned: '已指派',
    in_progress: '进行中',
    completed: '已完成',
    cancelled: '已取消'
  }
  return map[s] || s
}

function statusTag(s) {
  const map = {
    draft: 'info',
    assigned: 'primary',
    in_progress: 'warning',
    completed: 'success',
    cancelled: 'danger'
  }
  return map[s] || ''
}

/** 与桌面/筛选一致：流程已完成但本分卷未齐、且尚未仓库完工时，不显示「已完成」 */
function dispatchSlittingIncomplete(row) {
  if (!row || row.status !== 'completed') return false
  // 仓库已提交完工：历史单可能缺本分卷时间戳，不再显示为「进行中」
  if (row.warehouse_completed_at) return false
  const t = Number(row.mobile_slitting_total_lines ?? row.item_count ?? 0)
  const c = Number(row.mobile_slitting_confirmed_count ?? 0)
  if (!Number.isFinite(t) || t <= 0) return false
  return c < t
}

function cardStatusText(row) {
  if (postAuditPendingBadge(row)) return '待核销'
  return dispatchSlittingIncomplete(row) ? '进行中' : statusText(row.status)
}

function cardStatusTag(row) {
  if (postAuditPendingBadge(row)) return 'warning'
  return dispatchSlittingIncomplete(row) ? 'warning' : statusTag(row.status)
}

function auditText(a) {
  const map = { pending: '待审核', approved: '已通过', rejected: '已拒绝' }
  return map[a] || a || '—'
}

function auditTag(a) {
  const map = { pending: 'warning', approved: 'success', rejected: 'danger' }
  return map[a] || 'info'
}

/** 仓库已提交、业务员待第二轮核销（加工出库分码不走此核销） */
function postAuditPendingBadge(row) {
  if (!row || row.status !== 'completed') return false
  if (row.is_processing_outbound || row.dispatch_purpose === 'processing_outbound') return false
  return row.post_completion_audit_status === 'pending'
}

/** 列表卡片：本分卷确认条数 / 明细条数（与后端 mobile_slitting_* 一致） */
function slittingProgressTotals(row) {
  if (!row) return { total: 0, done: 0 }
  const total = Number(row.mobile_slitting_total_lines ?? row.item_count ?? 0)
  const done = Number(row.mobile_slitting_confirmed_count ?? 0)
  const t = Number.isFinite(total) && total > 0 ? Math.floor(total) : 0
  const d = Number.isFinite(done) && done >= 0 ? Math.floor(done) : 0
  return { total: t, done: d }
}

function slittingProgressShow(row) {
  return slittingProgressTotals(row).total > 0
}

function slittingProgressDone(row) {
  const { total, done } = slittingProgressTotals(row)
  return total > 0 && done >= total
}

function slittingProgressFraction(row) {
  const { total, done } = slittingProgressTotals(row)
  if (total <= 0) return '—'
  return `${Math.min(done, total)}/${total}`
}

/** 列表卡片副标题：客户 + 条数或单条时的订单号（避免长摘要占屏） */
function mobileDispatchCardOneLine(row) {
  if (!row) return '—'
  const cust = String(row.customer_name || '').trim() || '—'
  const n = Number(row.item_count) || 0
  let base = ''
  if (n > 1) base = `${cust} · ${n}条明细`
  else {
    const orderNo = String(row.order_no || '').trim()
    base = orderNo ? `${cust} · ${orderNo}` : cust
  }
  const extra = [row.seasons_display, row.order_types_display].filter(Boolean).join(' · ')
  return extra ? `${base} · ${extra}` : base
}

function fmtNumMaybe1(v) {
  if (v == null || Number.isNaN(Number(v))) return '0'
  const n = Number(v)
  const r = Math.round(n * 10) / 10
  if (Math.abs(r) < 0.0001) return '0'
  return r % 1 === 0 ? String(r) : r.toFixed(1)
}

function internalColorLine(oi) {
  const cn = oi.internal_color_cn || ''
  const en = oi.internal_color_en || ''
  if (!cn && !en) return ''
  return [cn && `中 ${cn}`, en && `英 ${en}`].filter(Boolean).join(' · ')
}

function matchingColorLine(mm) {
  const cn = mm.material_color_name_cn || ''
  const en = mm.material_color_name_en || ''
  if (!cn && !en) return ''
  return [cn && `中 ${cn}`, en && `英 ${en}`].filter(Boolean).join(' · ')
}

function mergedColor(oi, mm) {
  const a = internalColorLine(oi)
  const b = matchingColorLine(mm)
  if (!a && !b) return '—'
  if (a === b) return a || b
  if (!a) return b
  if (!b) return a
  return `${a} / ${b}`
}

/** 库位：编码/名称 + 物理数量 */
function formatLocStockLinesSimple(locs) {
  if (!Array.isArray(locs) || !locs.length) return []
  return locs.map((l) => {
    const code = (l.location_code || '').trim()
    const name = (l.location_name || '').trim()
    let label = code
    if (name && name !== code) {
      label = code ? `${code}(${name})` : name
    }
    if (!label) label = '库位'
    const phy = l.physical_quantity != null ? fmtNumMaybe1(l.physical_quantity) : '—'
    return `${label} 物理 ${phy}`
  })
}

function materialAggKey(it) {
  const mgk = it.merge_group_key
  if (mgk != null && String(mgk).trim() !== '') return `mg:${String(mgk).trim()}`
  const oi = it.order_item || {}
  const mm = oi.material_matching || {}
  const wh = String(it.warehouse_manual_code || it.material_code || mm.warehouse_manual_code || '').trim()
  if (wh && wh !== '未分配') return `wh:${wh}`
  if (mm.material_batch_id) return `batch:${mm.material_batch_id}`
  if (mm.material_identity_id) return `mid:${mm.material_identity_id}`
  return `_doi:${it.dispatch_order_item_id}`
}

function resolveWhCode(it) {
  const oi = it.order_item || {}
  const mm = oi.material_matching || {}
  return (
    it.warehouse_manual_code ||
    it.material_code ||
    mm.warehouse_manual_code ||
    mm.material_identity_code ||
    '—'
  )
}

/** 简述：内部中文材料名（品类） + 规格，不含客户材料名 */
function briefInternalSpec(oi, mm) {
  const name = String(oi.internal_product_name || mm.material_category_name || '').trim()
  const spec = String(oi.specification || mm.material_specification || '').trim()
  const parts = [name, spec].filter(Boolean)
  return parts.length ? parts.join(' · ') : '—'
}

function mergeLocationsInto(map, locs) {
  if (!Array.isArray(locs)) return
  for (const l of locs) {
    const id = l.location_id
    if (id == null) continue
    if (!map.has(id)) {
      map.set(id, {
        location_code: l.location_code || '',
        location_name: l.location_name || l.location_code || '库位',
        physical_quantity: l.physical_quantity
      })
    }
  }
}

/** 同一仓库料号（或同批次/身份）合并一行，需求数量 20+10=30，并汇总各明细用料备注 */
function buildMaterialRows(orderData) {
  const list = orderData?.items || []
  const groups = new Map()
  for (const it of list) {
    const key = materialAggKey(it)
    const oi = it.order_item || {}
    const mm = oi.material_matching || {}
    const locs = it.inventory_locations || mm.inventory_locations || []
    const qty = Number(oi.quantity) || 0
    const unit = (oi.unit_name || '').trim() || '单位'

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        whCode: resolveWhCode(it),
        brief: briefInternalSpec(oi, mm),
        colorLine: mergedColor(oi, mm),
        locMap: new Map(),
        qtyParts: [],
        unit,
        unitMixed: false,
        remarkLines: []
      })
    }
    const g = groups.get(key)
    mergeLocationsInto(g.locMap, locs)
    if (g.unit && unit && g.unit !== unit) g.unitMixed = true
    if (!g.unit && unit) g.unit = unit
    g.qtyParts.push(qty)

    const orderNo = (it.order_no || '').trim()
    const mun = String(it.material_usage_notes || '').trim()
    if (mun) {
      const left = orderNo ? `订单 ${orderNo}` : '明细'
      g.remarkLines.push(`${left} — ${mun}`)
    }
  }

  let rowNo = 0
  const rows = []
  for (const g of groups.values()) {
    rowNo += 1
    const partsStr = g.qtyParts.map((q) => fmtNumMaybe1(q)).join('+')
    const sum = g.qtyParts.reduce((a, b) => a + b, 0)
    let required = `${partsStr}=${fmtNumMaybe1(sum)} ${g.unit || ''}`.trim()
    if (g.unitMixed) required += '（各明细单位不一致时请以明细为准）'

    rows.push({
      key: g.key,
      rowNo,
      brief: g.brief,
      whCode: g.whCode,
      colorLine: g.colorLine,
      stockLines: formatLocStockLinesSimple(Array.from(g.locMap.values())),
      required,
      remarkLines: g.remarkLines
    })
  }
  return rows
}

async function openMaterials(row) {
  const did = row.dispatch_order_id
  materialsTitle.value = row.dispatch_order_no || String(did)
  materialsRows.value = []
  materialsDispatchNotes.value = ''
  materialsOpeningId.value = did
  try {
    const params = { require_audit_approved: 'true' }
    if (onlyMine.value) params.enforce_my_dispatch = 'true'
    const res = await getDispatchOrder(did, { params })
    if (res.success && res.data) {
      materialsDispatchNotes.value = String(res.data.notes || '').trim()
      materialsRows.value = buildMaterialRows(res.data)
      // 数据就绪后再打开弹窗，减轻部分机型上 Dialog + 异步内容的合成层白屏
      await nextTick()
      materialsVisible.value = true
    } else {
      ElMessage.error(res.message || '无法加载材料明细')
    }
  } catch (e) {
    const st = e.response?.status
    const msg = e.response?.data?.message || e.message || '加载失败'
    if (st === 403) {
      ElMessage.warning(msg)
    } else {
      ElMessage.error(msg)
    }
  } finally {
    materialsOpeningId.value = null
  }
}

function onMaterialsClosed() {
  materialsRows.value = []
  materialsDispatchNotes.value = ''
}

async function load() {
  loading.value = true
  try {
    const params = {
      page: pagination.page,
      per_page: pagination.per_page,
      require_audit_approved: 'true'
    }
    if (onlyMine.value) params.assigned_to_me = 'true'
    if (filterStatus.value === 'pending_post_audit') {
      params.status = 'completed'
      params.post_completion_audit_status = 'pending'
    } else if (filterStatus.value) {
      params.status = filterStatus.value
    }
    if (onlyIncomplete.value && filterStatus.value !== 'completed') {
      params.exclude_fully_completed_cards = 'true'
    }
    if (keyword.value.trim()) params.search = keyword.value.trim()
    params.sort = listSort.value
    const res = await getDispatchOrders(params)
    if (!res.success) {
      ElMessage.error(res.message || '加载失败')
      return
    }
    rows.value = res.data || []
    const p = res.pagination || {}
    pagination.total = p.total || 0
    pagination.pages = p.pages || 1
    pagination.page = p.page || pagination.page
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '加载失败')
  } finally {
    loading.value = false
  }
}

function reload() {
  pagination.page = 1
  load()
}

function pagePrev() {
  if (pagination.page <= 1) return
  pagination.page -= 1
  load()
}

function pageNext() {
  if (pagination.page >= pagination.pages) return
  pagination.page += 1
  load()
}

function openDispatchDetail(row) {
  if (!canOpenDispatchDetail.value) {
    ElMessage.warning('无权进入派工详情')
    return
  }
  router.push(mPath(`dispatch/${row.dispatch_order_id}`))
}

function openDispatchPackaging(row) {
  router.push(mPath(`dispatch/${row.dispatch_order_id}/packaging`))
}

/** 缺称重/外尺寸 → 橙色；已全部录入 → 灰色 */
function packagingMeasuresIncomplete(row) {
  if (!row?.packaging_require_any) return false
  if (row.packaging_measures_incomplete != null) return !!row.packaging_measures_incomplete
  return true
}

onMounted(() => {
  userStore.initFromStorage()
  initListSortFromStorage()
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}')
    const isPrivileged = Boolean(u.is_admin) || Boolean(u.is_superuser)
    onlyMine.value = !isPrivileged
  } catch {
    onlyMine.value = true
  }
  load()
})

let listActivatedOnce = false
onActivated(() => {
  if (!listActivatedOnce) {
    listActivatedOnce = true
    return
  }
  load()
})
</script>

<style scoped>
.m-page {
  max-width: 720px;
  width: 100%;
  margin: 0 auto;
  overflow-x: hidden;
  box-sizing: border-box;
}

.m-toolbar {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
}

.m-toolbar__row {
  display: flex;
  align-items: stretch;
  gap: 8px;
}

.m-toolbar__search {
  flex: 1;
  min-width: 0;
}

.m-toolbar__refresh {
  flex-shrink: 0;
  align-self: stretch;
}

.m-switch-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px 20px;
}

.m-sort-select {
  width: 100%;
}

.m-mine-hint {
  margin: 0;
  font-size: 12px;
  color: #64748b;
  line-height: 1.45;
}

.m-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.m-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.m-card.readonly {
  border: 1px dashed #cbd5e1;
}

.m-card__main {
  padding: 14px 16px 10px;
  cursor: pointer;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.m-card__foot {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 12px 12px;
  border-top: 1px solid #f1f5f9;
  background: #fafbfc;
}

.m-pack-btn--done {
  --el-button-text-color: #64748b;
  --el-button-border-color: #cbd5e1;
  --el-button-bg-color: #f1f5f9;
  --el-button-hover-text-color: #475569;
  --el-button-hover-border-color: #94a3b8;
  --el-button-hover-bg-color: #e2e8f0;
}

.m-card__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 6px;
  min-width: 0;
  max-width: 100%;
}

.m-card__title > span:first-child,
.m-card__title > :first-child {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.m-card__title :deep(.el-tag) {
  flex-shrink: 0;
  max-width: 45%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.m-card__due {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
}

.m-card__due-text {
  color: #334155;
}

.m-card__due.m-due--critical .m-card__due-text {
  color: #7f1d1d;
  font-weight: 700;
}

.m-card__due.m-due--today .m-card__due-text {
  color: #fb7185;
  font-weight: 600;
}

.m-card__due.m-due--overdue .m-card__due-text {
  color: #9a3412;
  font-weight: 600;
}

.m-card__due.m-due--future .m-card__due-text {
  color: #15803d;
  font-weight: 600;
}

.m-card__due.m-due--completed-ok .m-card__due-text {
  color: #166534;
  font-weight: 600;
}

.m-card__line {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  color: #334155;
  margin-top: 4px;
}

.m-card__line.muted {
  color: #64748b;
}

.m-card__slitting .m-slitting-done {
  color: #16a34a;
  font-weight: 600;
}

.m-card__slitting .m-slitting-frac {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: #0f172a;
}

.m-readonly-text {
  color: #94a3b8;
  font-size: 12px;
}

.m-pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 16px;
}

.m-pager__info {
  font-size: 14px;
  color: #64748b;
}

.m-mat-scroll {
  min-height: 80px;
  max-height: min(70vh, 560px);
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

.m-mat-row {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 10px;
  font-size: 13px;
  color: #334155;
}

.m-mat-row__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-weight: 600;
}

.m-mat-idx {
  color: #64748b;
}

.m-mat-req {
  color: #0f172a;
}

.m-mat-line {
  margin-top: 4px;
  line-height: 1.45;
}

.m-mat-line .lbl {
  display: inline-block;
  min-width: 4.5em;
  color: #94a3b8;
  margin-right: 6px;
}

.m-mat-locs {
  margin-top: 4px;
  padding-left: 5em;
}

.m-mat-loc {
  font-size: 12px;
  color: #475569;
  margin-top: 2px;
}

.muted {
  color: #94a3b8;
}

.muted.sm {
  font-size: 12px;
  margin: 4px 0 0 5em;
}

.m-dispatch-notes {
  font-size: 13px;
  color: #334155;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 10px 12px;
  margin: 0 0 12px;
  line-height: 1.5;
}

.m-dispatch-notes--secondary {
  background: #f8fafc;
  border-color: #e2e8f0;
  color: #64748b;
}

.m-mat-hint {
  font-size: 12px;
  margin: 0 0 10px;
  line-height: 1.45;
}

.m-mat-line--mt {
  margin-top: 10px;
}

.m-mat-remark {
  font-size: 12px;
  color: #475569;
  margin: 4px 0 0 5em;
  line-height: 1.45;
}
</style>

<style>
/* 弹窗本体限制高度，避免 iOS Safari 与内部滚动叠加导致的闪白 */
.m-mat-dialog.el-dialog {
  display: flex;
  flex-direction: column;
  max-height: 92vh;
  margin: 12px auto !important;
}
.m-mat-dialog .el-dialog__body {
  flex: 1;
  min-height: 0;
  padding-top: 10px;
}
</style>
